import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './Painting.module.css';

// Data
const services = [
  { 
    title: "Interior Wall & Ceiling Painting",
    icon: "🎨"
  },
  { 
    title: "Exterior Weather-Proof Painting",
    icon: "🏡"
  },
  { 
    title: "Texture & Designer Wall Finishes",
    icon: "✨"
  },
  { 
    title: "Wood & Metal Polish/Paint",
    icon: "🪚"
  },
  { 
    title: "Rental Property Quick Repaint",
    icon: "⏱️"
  },
  { 
    title: "Post-construction Full Painting",
    icon: "🏗️"
  },
  { 
    title: "Damp-proof & Waterproof Coating",
    icon: "🛡️"
  },
  { 
    title: "Repainting for Resale & Home Staging",
    icon: "🏠"
  },
];

const addons = [
  "Wall putty and crack filling",
  "Surface sanding and preparation",
  "Masking, cleanup, and polish",
  "Waterproof primer coating",
  "Anti-fungal & odorless options",
];

const testimonials = [
  {
    name: 'Aarti D., Thane',
    quote: 'Their painting crew finished our entire 3BHK in 4 days — clean, professional, and no mess! I loved the finish.',
    rating: 5,
    avatar: '👩'
  },
  {
    name: 'Nishant K., Panaji',
    quote: 'We booked Property Plateau for repainting our rental flat. Quick service and made the place look brand new.',
    rating: 5,
    avatar: '👨'
  },
  {
    name: 'Rahul M., Bangalore',
    quote: 'The color consultation was spot on. Our home looks exactly how we envisioned - modern and fresh!',
    rating: 4,
    avatar: '👨‍💼'
  }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99]
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

// const pulseAnimation = {
//   scale: [1, 1.05, 1],
//   transition: {
//     duration: 2,
//     repeat: Infinity,
//     ease: "easeInOut"
//   }
// };

// Components
const ServiceCard = ({ title, icon }) => {
  const controls = useAnimation();
  
  return (
    <motion.div
      className={styles.card}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={fadeInUp}
      whileHover={{ 
        y: -10,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
      onHoverStart={() => controls.start("hover")}
      onHoverEnd={() => controls.start("initial")}
    >
      <motion.span 
        className={styles.cardIcon}
        animate={controls}
        variants={{
          initial: { scale: 1 },
          hover: { scale: 1.2 }
        }}
      >
        {icon}
      </motion.span>
      <h3 className={styles.cardTitle}>{title}</h3>
    </motion.div>
  );
};

const TestimonialCard = ({ name, quote, rating, avatar }) => (
  <motion.div 
    className={styles.testimonialCard}
    variants={fadeInUp}
    whileHover={{ scale: 1.02 }}
  >
    <div className={styles.testimonialHeader}>
      <span className={styles.avatar}>{avatar}</span>
      <div>
        <div className={styles.rating}>
          {[...Array(5)].map((_, i) => (
            <span key={i} className={i < rating ? styles.starFilled : styles.starEmpty}>★</span>
          ))}
        </div>
        <p className={styles.testimonialAuthor}>{name}</p>
      </div>
    </div>
    <p className={styles.testimonialQuote}>"{quote}"</p>
  </motion.div>
);

const AnimatedSection = ({ children, className = '' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

export default function PaintingServicesPage() {
  return (
    <div className={styles.pageContainer}>
      {/* Hero Section */}
      <section className={`${styles.section} ${styles.heroSection}`}>
        <div className={styles.heroContent}>
          <motion.h1 
            className={styles.headingPrimary}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
           From Dull to Dazzling  <span className={styles.highlight}>Transform Your Home with Color</span>
          </motion.h1>
          
          <motion.p 
            className={styles.heroText}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Give your property a vibrant, premium look with Property Plateau Realty’s expert painting services. Whether it’s a residential home, commercial space, or rental property, we deliver precision painting solutions that elevate the look and value of your space.


          </motion.p>
          
          <motion.div 
            className={styles.ctaButtons}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >

          </motion.div>
        </div>
        
 <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundImage: "url('/painting_hero.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat"
      }}
    />

 </section>

      {/* Why Choose Us */}
      <AnimatedSection className={`${styles.section} ${styles.sectionAlt}`}>
        <motion.h2 variants={fadeInUp} className={styles.headingSecondary}>
         Why Choose Property Plateau for Painting Services?

        </motion.h2>
        
        <div className={styles.featuresGrid}>
          {[
            {
              title: "Trained Painters & Quality Finishes",
              description: "Our team of certified painters and supervisors ensure smooth finishes, sharp lines, and perfect color tones—every single time",
              icon: "👨‍🎨"
            },
            {
              title: "Premium-Grade Materials",
              description: "We use only trusted brands like Asian Paints, Berger, Nerolac, and Dulux, ensuring durability, color vibrancy, and a premium touch",
              icon: "🎨"
            },
            {
              title: "Quick Turnaround, Minimal Disruption",
              description: "Our process is swift, clean, and professional—ideal for both occupied homes and new projects. We protect your furniture and complete the job on time",
              icon: "⏱️"
            },
            {
              title: "Interior & Exterior Painting",
              description: "From walls and ceilings to balconies and exteriors, we use weather-resistant and washable paints for long-lasting appeal",
              icon: "🧹"
            },
            {
              title: "Customized Color Consultation",
              description: "Not sure what colors suit your vibe? Our experts offer free color consulting, helping you choose shades that match your interior design and energy",
              icon: "🎨"
            }
           
          ].map((feature, index) => (
            <motion.div 
              key={index} 
              className={styles.featureCard}
              variants={fadeInUp}
              whileHover={{ y: -5 }}
            >
              <div className={styles.featureIcon}>{feature.icon}</div>
              <h3 className={styles.featureTitle}>{feature.title}</h3>
              <p className={styles.featureText}>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>

      {/* Services Section */}
      <AnimatedSection className={styles.section}>
        <motion.h2 variants={fadeInUp} className={styles.headingSecondary}>
          Our Comprehensive Painting Services
        </motion.h2>
        
        <motion.div variants={staggerContainer} className={styles.servicesGrid}>
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </motion.div>

        <motion.h3 variants={fadeInUp} className={styles.headingTertiary}>
          Premium Add-On Services
        </motion.h3>
        
        <motion.div variants={staggerContainer} className={styles.addonsGrid}>
          {addons.map((addon, index) => (
            <motion.div 
              key={index} 
              className={styles.addonCard}
              variants={fadeInUp}
              whileHover={{ scale: 1.05 }}
            >
              {addon}
            </motion.div>
          ))}
        </motion.div>
      </AnimatedSection>

      {/* Testimonials */}
      <AnimatedSection className={`${styles.section} ${styles.testimonialsSection}`}>
        <motion.h2 variants={fadeInUp} className={styles.headingSecondary}>
          Trusted by Homeowners Across India
        </motion.h2>
        
        <motion.div variants={staggerContainer} className={styles.testimonialsGrid}>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} {...testimonial} />
          ))}
        </motion.div>
      </AnimatedSection>

      {/* Service Areas */}
      <AnimatedSection className={styles.section}>
        <motion.h2 variants={fadeInUp} className={styles.headingSecondary}>
          Serving Major Cities Across India
        </motion.h2>
        
        <motion.div variants={fadeInUp} className={styles.citiesGrid}>
          {['Mumbai', 'Pune', 'Goa', 'Bangalore', 'Hyderabad', 'Delhi-NCR', 'Chennai', 'Kolkata'].map((city, index) => (
            <motion.div 
              key={index} 
              className={styles.cityCard}
              whileHover={{ scale: 1.05 }}
            >
              {city}
            </motion.div>
          ))}
        </motion.div>
        
        <motion.p variants={fadeInUp} className={styles.serviceAreaText}>
          Available for bulk projects nationwide with special rates for developers
        </motion.p>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className={`${styles.section} ${styles.ctaSection}`}>
        <motion.div variants={fadeInUp} className={styles.ctaContainer}>
          <h2 className={styles.ctaHeading}>Ready to Transform Your Space?</h2>
          <p className={styles.ctaText}>
            Get a free, no-obligation quote today and see how we can bring color to your world.
          </p>
          
          <motion.div 
            className={styles.ctaButtons}
            variants={fadeInUp}
          >
           
            <motion.button 
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnLarge}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
             [Get Instant Color Consultation]

            </motion.button>
          </motion.div>
        </motion.div>
      </AnimatedSection>
    </div>
  );
}